// Calls history endpoint. Drives apps/web's /calls route.
//
// BACKEND CONTRACT (see docs/architecture/backend-contracts.md):
//   GET /v1/calls/history?limit=<n>&cursor=<opaque>
//        → { calls: CallHistoryEntry[], nextCursor?: string }
//   POST /v1/calls/start  { calleeId, kind: "audio"|"video" }
//        → { callId: string }
//
// Returns empty by default until BE ships; opt-in via
// `VITE_WITH_MOCK=true` (see core/network/mockFlag.ts) gets a
// deterministic stub timeline so the UI has something to render.

import { del, get, NetworkError, post } from './api';
import { WITH_MOCK } from './mockFlag';
import type { DirectoryUser } from './users';
import { getUserProfiles, resolveMediaUrl, searchUsers } from './users';

export type CallDirection = 'incoming' | 'outgoing' | 'missed';
export type CallKind = 'audio' | 'video';

/** One resolved participant for a group-call avatar stack. */
export interface CallParticipantSummary {
  readonly displayName: string;
  readonly avatarUri?: string;
}

export interface CallHistoryEntry {
  readonly id: string;
  readonly user: DirectoryUser;
  readonly direction: CallDirection;
  readonly kind: CallKind;
  readonly timestamp: number;
  readonly durationSec?: number;
  /** Full roster for a group call (drives the avatar stack); absent for a 1:1. */
  readonly participants?: readonly CallParticipantSummary[];
}

// Shape the backend actually returns — `user` is not embedded;
// only the peer's account_id (+ optional group roster) is included.
interface RawCallHistoryEntry {
  readonly id: string;
  readonly peerAccountId: string;
  readonly participantAccountIds?: readonly string[];
  readonly direction: CallDirection;
  readonly kind: CallKind;
  readonly timestamp: number;
  readonly durationSec?: number;
}

interface RawCallHistoryResponse {
  readonly calls: readonly RawCallHistoryEntry[];
  readonly nextCursor?: string | undefined;
}

export interface CallHistoryResponse {
  readonly calls: readonly CallHistoryEntry[];
  readonly nextCursor?: string | undefined;
}

export interface StartCallResponse {
  readonly callId: string;
}

const MIN = 60_000;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;

function isBackendMissing(err: unknown): boolean {
  return err instanceof NetworkError && (err.status === 404 || err.status === 501);
}

async function devFallbackHistory(): Promise<CallHistoryResponse> {
  const res = await searchUsers({ query: '', limit: 50 });
  const users = res.users;
  const now = Date.now();
  const directions: CallDirection[] = ['incoming', 'outgoing', 'missed'];
  const kinds: CallKind[] = ['audio', 'video', 'audio', 'video'];
  const calls: CallHistoryEntry[] = users.slice(0, 8).map((u, i): CallHistoryEntry => {
    const base = {
      id: `call-${u.id}`,
      user: u,
      direction: directions[i % directions.length]!,
      kind: kinds[i % kinds.length]!,
      timestamp: now - (i === 0 ? 12 * MIN : i === 1 ? 47 * MIN : i === 2 ? 3 * HOUR : i * DAY),
    } satisfies Omit<CallHistoryEntry, 'durationSec'>;
    return i % 3 === 2 ? base : { ...base, durationSec: 60 + i * 47 };
  });
  return { calls };
}

async function enrichRawCalls(
  raw: readonly RawCallHistoryEntry[],
): Promise<readonly CallHistoryEntry[]> {
  // One batch request for every unique account across peers AND group rosters,
  // then map each to a `DirectoryUser`. The avatar comes back as a media id —
  // it MUST be resolved to a fetchable download URL before it can feed
  // `<Avatar imageUri>` (the raw id renders nothing and the row silently falls
  // back to initials). Same rule the search/lookup mappers follow.
  const uniqueIds = [
    ...new Set(
      raw.flatMap((e) => [e.peerAccountId, ...(e.participantAccountIds ?? [])]),
    ),
  ].filter((id) => id.length > 0);
  const profiles = await getUserProfiles(uniqueIds);
  const profileMap = new Map<string, DirectoryUser>();
  await Promise.all(
    uniqueIds.map(async (accountId) => {
      const profile = profiles.get(accountId);
      const avatarUri = profile?.avatarMediaId
        ? await resolveMediaUrl(profile.avatarMediaId)
        : undefined;
      const user: DirectoryUser = {
        id: accountId,
        username: accountId,
        displayName: profile?.displayName ?? accountId,
        ...(avatarUri ? { avatarUri } : {}),
        ...(profile?.jobTitle ? { role: profile.jobTitle } : {}),
        ...(profile?.companyName ? { company: profile.companyName } : {}),
      };
      profileMap.set(accountId, user);
    }),
  );
  const userFor = (id: string): DirectoryUser =>
    profileMap.get(id) ?? { id, username: id, displayName: id };
  return raw.map((e) => {
    const roster = e.participantAccountIds ?? [];
    return {
      id: e.id,
      user: userFor(e.peerAccountId),
      direction: e.direction,
      kind: e.kind,
      timestamp: e.timestamp,
      ...(e.durationSec !== undefined ? { durationSec: e.durationSec } : {}),
      ...(roster.length > 1
        ? {
            participants: roster.map((id) => {
              const u = userFor(id);
              return u.avatarUri !== undefined
                ? { displayName: u.displayName, avatarUri: u.avatarUri }
                : { displayName: u.displayName };
            }),
          }
        : {}),
    };
  });
}

export async function loadCallHistory(
  params: {
    limit?: number;
    cursor?: string;
    signal?: AbortSignal;
  } = {},
): Promise<CallHistoryResponse> {
  try {
    const qs = new URLSearchParams();
    if (params.limit !== undefined) qs.set('limit', String(params.limit));
    if (params.cursor !== undefined) qs.set('cursor', params.cursor);
    const opts = params.signal ? { signal: params.signal } : {};
    const raw = await get<RawCallHistoryResponse>(`/v1/calls/history?${qs}`, opts);
    const calls = await enrichRawCalls(raw.calls);
    return { calls, ...(raw.nextCursor !== undefined ? { nextCursor: raw.nextCursor } : {}) };
  } catch (err) {
    if (WITH_MOCK && (isBackendMissing(err) || !(err instanceof NetworkError))) {
      return devFallbackHistory();
    }
    if (isBackendMissing(err)) {
      return { calls: [] };
    }
    throw err;
  }
}

export async function startCall(params: {
  calleeId: string;
  kind: CallKind;
}): Promise<StartCallResponse> {
  try {
    return await post<StartCallResponse>('/v1/calls/start', {
      calleeId: params.calleeId,
      kind: params.kind,
    });
  } catch (err) {
    if (WITH_MOCK && isBackendMissing(err)) {
      return { callId: `local-${params.calleeId}-${Date.now()}` };
    }
    throw err;
  }
}

// --- LiveKit token + end (backend: services/api/internal/calls) -----------

export interface CallTokenResponse {
  /** LiveKit access token (JWT) for room = call_id. */
  readonly token: string;
  /** LiveKit Cloud websocket URL to dial. */
  readonly url: string;
  /** RFC3339 expiry. */
  readonly expires_at: string;
  /** True when the server has an S3 sink configured so /recording works. */
  readonly recording_enabled?: boolean;
}

/**
 * Mint a LiveKit room token for `callId` (lowercase-hex, 16 bytes). The
 * backend verifies the caller's device is a member of `callGroupId` (the
 * conversation hosting the call) and signs the token with the LiveKit API
 * secret. `displayName` becomes the participant tile label.
 */
export async function mintCallToken(params: {
  callId: string;
  callGroupId: string;
  conversationId?: string;
  kind: CallKind;
  displayName?: string;
}): Promise<CallTokenResponse> {
  return post<CallTokenResponse>(`/v1/calls/${encodeURIComponent(params.callId)}/token`, {
    call_group_id: params.callGroupId,
    ...(params.conversationId !== undefined ? { conversation_id: params.conversationId } : {}),
    kind: params.kind,
    ...(params.displayName !== undefined ? { display_name: params.displayName } : {}),
  });
}

/** Start workspace recording (LiveKit Egress → S3). Throws if not configured. */
export async function startRecording(params: {
  callId: string;
  callGroupId: string;
}): Promise<{ egressId: string }> {
  const res = await post<{ egress_id: string }>(
    `/v1/calls/${encodeURIComponent(params.callId)}/recording`,
    { call_group_id: params.callGroupId },
  );
  return { egressId: res.egress_id };
}

/** Stop workspace recording. */
export async function stopRecording(params: { callId: string }): Promise<void> {
  await del<void>(`/v1/calls/${encodeURIComponent(params.callId)}/recording`);
}

/**
 * Record the terminal state of a call for history. Best-effort — callers
 * should not block hangup UX on this resolving.
 */
export async function endCall(params: {
  callId: string;
  reason?: string;
  durationMs?: number;
}): Promise<void> {
  try {
    await post<void>(`/v1/calls/${encodeURIComponent(params.callId)}/end`, {
      ...(params.reason !== undefined ? { reason: params.reason } : {}),
      ...(params.durationMs !== undefined ? { duration_ms: params.durationMs } : {}),
    });
  } catch {
    // History is non-critical; swallow.
  }
}
